import { uniqueId } from 'lodash'
import React, { useContext, useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player'
import { usePrevious } from 'react-use'
import DotsLoader from 'shared/components/loaders/dots'
import styled from 'styled-components'
import { useCreationSourcePreviewUrl } from '../hooks/use-creation-source-preview-url'
import { CreationToolsContext } from '../manager'
import { CreationToolSourceFile } from '../types'
import { SquareButton } from './buttons'

const Container = styled.div`
    background: #000;
    overflow: hidden;
    position: relative;
    width: 100%;

    iframe {
        display: block;
        height: 200%;
        transform: translateY(-25%);
        width: 100%;
    }

    video {
        display: block;
        object-fit: cover;
        width: 100%;
    }
`

const VolumeButton = styled(SquareButton)`
    bottom: 10px;
    position: absolute;
    right: 10px;
`

type Props = {
    className?: string
    source: CreationToolSourceFile
    startTime?: number
    endTime?: number
    hideAudioIcon?: boolean
    width: number
    onTimeUpdate?: (currentTime: number) => void
}

const CreationToolsVideoPlayer = ({
    className,
    source,
    startTime,
    endTime,
    width,
    hideAudioIcon = false,
    onTimeUpdate,
}: Props) => {
    const { currentAudioId, setCurrentAudioId } = useContext(CreationToolsContext)
    const playerRef = useRef<any>(null)
    const playerIdRef = useRef<string>(uniqueId('videoplayer'))
    const pauseTimeoutRef = useRef<null | ReturnType<typeof setTimeout>>(null)
    const prevStartTime = usePrevious(startTime)
    const prevEndTime = usePrevious(endTime)
    const [isMuted, setIsMuted] = useState<boolean>(true)
    const [isPlaying, setIsPlaying] = useState<boolean>(true)
    const videoPreviewUrl = useCreationSourcePreviewUrl(source.source)

    useEffect(() => {
        // if an audio source id is set and it's not this player, mute it
        if (currentAudioId !== playerIdRef.current) {
            setIsMuted(true)
        }
    }, [currentAudioId])

    useEffect(() => {
        if (typeof startTime === 'undefined' || typeof endTime === 'undefined') {
            return
        }

        if (startTime !== prevStartTime) {
            jumpToTime(startTime)
        } else if (endTime !== prevEndTime) {
            jumpToTime(endTime)
        }
    }, [startTime, prevStartTime, endTime, prevEndTime])

    const jumpToTime = (time: number) => {
        if (pauseTimeoutRef.current) {
            clearTimeout(pauseTimeoutRef.current)
        }

        if (playerRef.current) {
            setIsPlaying(false)
            playerRef.current.seekTo(time, 'seconds')
            pauseTimeoutRef.current = setTimeout(() => {
                setIsPlaying(true)
            }, 1000)
        }
    }

    const onProgress = () => {
        const player = playerRef.current
        const ignoreProgress = !endTime && !onTimeUpdate

        if (!player || ignoreProgress) return

        const currentTime = player.getCurrentTime()
        onTimeUpdate && onTimeUpdate(currentTime)

        // manual loop
        if ((startTime && currentTime < startTime) || (endTime && currentTime >= endTime)) {
            player.seekTo(startTime, 'seconds')
        }
    }

    const onMuteToggle = () => {
        const isMuting = !isMuted
        setIsMuted(isMuting)
        if (!isMuting) {
            setCurrentAudioId(playerIdRef.current)
        }
    }

    return !videoPreviewUrl ? (
        <DotsLoader />
    ) : (
        <Container className={className}>
            <ReactPlayer
                {...{
                    ref: playerRef,
                    url: videoPreviewUrl,
                    muted: isMuted,
                    playing: isPlaying,
                    loop: true,
                    playsinline: true,
                    progressInterval: 50,
                    height: width / (source.width / source.height),
                    width,
                    onProgress,
                }}
            />
            {!hideAudioIcon && source.hasAudio && (
                <VolumeButton onClick={onMuteToggle}>
                    <i className={isMuted ? 'ss-volume' : 'ss-highvolume'} />
                </VolumeButton>
            )}
        </Container>
    )
}

export default CreationToolsVideoPlayer
